import { X, Music, Clock, Zap, Volume2, Edit3, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import Button from '../common/Button';
import VideoCreator from '../video/VideoCreator';
import CulturalSelector from '../video/CulturalSelector';
import { api } from '../../services/api';
import type { TrackContext } from '../../types';

/**
 * Get a clean display name for a track, hiding UUIDs and timestamps
 */
function getTrackDisplayName(track: TrackContext): string {
  if (track.display_name) return track.display_name;
  if (track.filename) {
    const parts = track.filename.split('-');
    if (parts.length >= 3) {
      return parts.slice(2).join('-').replace(/_/g, ' ').replace(/\.[^/.]+$/, '');
    }
    return track.filename.replace(/_/g, ' ').replace(/\.[^/.]+$/, '');
  }
  return 'Untitled Track';
}

interface TrackDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  track: TrackContext;
  personaId: string;
  onLyricsUpdate?: (lyrics: string) => void;
  onUploadAnother?: () => void;
}

export default function TrackDetailModal({ isOpen, onClose, track, personaId, onLyricsUpdate, onUploadAnother }: TrackDetailModalProps) {
  const [isEditingLyrics, setIsEditingLyrics] = useState(false);
  const [editedLyrics, setEditedLyrics] = useState('');
  const [isVideoCreatorOpen, setIsVideoCreatorOpen] = useState(false);
  const [selectedCulture, setSelectedCulture] = useState<string | null>(track.cultural_context || null);
  const [isSavingCulture, setIsSavingCulture] = useState(false);

  // Sync selected culture with track data when it changes
  useEffect(() => {
    setSelectedCulture(track.cultural_context || null);
  }, [track.cultural_context]);

  // Save cultural context when selection changes
  const handleCultureSelect = async (cultureCode: string | null) => {
    setSelectedCulture(cultureCode);
    setIsSavingCulture(true);
    try {
      await api.updateTrackCulture(track.id, cultureCode);
    } catch (err) {
      console.error('Failed to save cultural context:', err);
    }
    setIsSavingCulture(false);
  };

  if (!isOpen) return null;

  // Extract data from context_data
  const contextData = track.context_data as Record<string, unknown> || {};
  const transcription = contextData.transcription as Record<string, unknown> || {};
  const aiAnalysis = contextData.ai_analysis as Record<string, unknown> || {};
  const musicalIntelligence = aiAnalysis.musical_intelligence as Record<string, unknown> || {};
  const visualRecommendations = aiAnalysis.visual_recommendations as Record<string, unknown> || {};

  const lyrics = transcription.text as string || '';
  const summary = musicalIntelligence.summary as string || aiAnalysis.summary as string || '';

  // Extract visual recommendations with proper typing
  const colorPalette = visualRecommendations.color_palette ? String(visualRecommendations.color_palette) : '';
  const visualStyle = visualRecommendations.visual_style ? String(visualRecommendations.visual_style) : '';
  const motionType = visualRecommendations.motion_type ? String(visualRecommendations.motion_type) : '';
  const moodVisualization = visualRecommendations.mood_visualization ? String(visualRecommendations.mood_visualization) : '';

  const handleStartEditLyrics = () => {
    setEditedLyrics(lyrics);
    setIsEditingLyrics(true);
  };

  const handleSaveLyrics = () => {
    onLyricsUpdate?.(editedLyrics);
    setIsEditingLyrics(false);
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-jelly-500/20 flex items-center justify-center">
              <Music className="w-5 h-5 text-jelly-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold truncate max-w-md">{getTrackDisplayName(track)}</h2>
              <p className="text-xs text-zinc-500">
                {track.primary_genre || 'Unknown genre'} • {track.primary_mood || 'Unknown mood'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Clock className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
              <p className="text-sm font-medium">{formatDuration(track.duration_seconds)}</p>
              <p className="text-xs text-zinc-500">Duration</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Zap className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
              <p className="text-sm font-medium">{track.tempo_bpm ? Math.round(track.tempo_bpm) : '--'}</p>
              <p className="text-xs text-zinc-500">BPM</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Volume2 className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
              <p className="text-sm font-medium">{track.key_signature || '--'}</p>
              <p className="text-xs text-zinc-500">Key</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Music className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
              <p className="text-sm font-medium truncate">{track.primary_mood || '--'}</p>
              <p className="text-xs text-zinc-500">Mood</p>
            </div>
          </div>

          {/* AI Summary */}
          {summary && (
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2 text-jelly-400">AI Summary</h3>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{summary}</p>
            </div>
          )}

          {/* Lyrics */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-jelly-400">Lyrics</h3>
              {!isEditingLyrics ? (
                <button
                  onClick={handleStartEditLyrics}
                  className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" />
                  Edit
                </button>
              ) : (
                <button
                  onClick={handleSaveLyrics}
                  className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              )}
            </div>
            {isEditingLyrics ? (
              <textarea
                value={editedLyrics}
                onChange={(e) => setEditedLyrics(e.target.value)}
                className="w-full h-48 bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-200 resize-none focus:outline-none focus:border-jelly-500"
                placeholder="Enter or edit lyrics..."
              />
            ) : (
              <div className="max-h-48 overflow-y-auto">
                {lyrics ? (
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{lyrics}</p>
                ) : (
                  <p className="text-sm text-zinc-500 italic">No lyrics detected. Click Edit to add manually.</p>
                )}
              </div>
            )}
          </div>

          {/* Visual Recommendations */}
          {(colorPalette || visualStyle || motionType || moodVisualization) && (
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2 text-jelly-400">Visual Recommendations</h3>
              <div className="space-y-2 text-sm text-zinc-300">
                {colorPalette && (
                  <p><span className="text-zinc-500">Colors:</span> {colorPalette}</p>
                )}
                {visualStyle && (
                  <p><span className="text-zinc-500">Style:</span> {visualStyle}</p>
                )}
                {motionType && (
                  <p><span className="text-zinc-500">Motion:</span> {motionType}</p>
                )}
                {moodVisualization && (
                  <p><span className="text-zinc-500">Mood:</span> {moodVisualization}</p>
                )}
              </div>
            </div>
          )}

          {/* Cultural Context Selection */}
          <CulturalSelector
            selectedCulture={selectedCulture}
            onSelect={handleCultureSelect}
            className={isSavingCulture ? 'opacity-70 pointer-events-none' : ''}
          />

          {/* Video Prompt Preview */}
          <div className="bg-gradient-to-r from-jelly-500/10 to-purple-500/10 border border-jelly-500/20 rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-2 text-jelly-400">Video Prompt Preview</h3>
            <p className="text-sm text-zinc-300">
              Create a music video for a <span className="text-jelly-300">{track.primary_genre || 'music'}</span> track
              with a <span className="text-jelly-300">{track.primary_mood || 'dynamic'}</span> mood
              at <span className="text-jelly-300">{track.tempo_bpm ? Math.round(track.tempo_bpm) : 120} BPM</span>.
              {lyrics && (
                <> The lyrics explore themes of: <span className="text-jelly-300">"{lyrics.slice(0, 100)}..."</span></>
              )}
              {visualStyle && (
                <> Visual style should be <span className="text-jelly-300">{visualStyle}</span>.</>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-zinc-800">
          <div>
            {onUploadAnother && (
              <Button variant="secondary" onClick={() => { onClose(); onUploadAnother(); }}>
                Upload Another
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button onClick={() => setIsVideoCreatorOpen(true)}>Create Video</Button>
          </div>
        </div>
      </div>

      {/* Video Creator Modal */}
      <VideoCreator
        isOpen={isVideoCreatorOpen}
        onClose={() => setIsVideoCreatorOpen(false)}
        track={track}
        personaId={personaId}
        initialCulturalContext={selectedCulture}
        onComplete={(videoUrl) => {
          console.log('Video created:', videoUrl);
          setIsVideoCreatorOpen(false);
        }}
      />
    </div>
  );
}
